import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { QueryBuilderComponent, RuleChangeEventArgs, RuleModel, QueryBuilderModule, QueryLibraryService, ColumnsModel } from '@syncfusion/ej2-angular-querybuilder';
import { FilterCondition, FilterDTO, FilterOperator } from 'src/app/base/models/models.swagger.generated';
import { SyncfusionTranslationService } from 'src/app/base/services/syncfusion-translation.service';
import { GridModule } from '@syncfusion/ej2-angular-grids';
import { FieldAssignmentDefinitionsService } from '../../../field-assignment-definitions.service';
import { ImportConfigurationEditFormsCoordinatorService } from '../../import-configuration-edit-forms-coordinator.service';

@Component({
    selector: 'app-file-filter',
    templateUrl: './file-filter.component.html',
    styleUrls: ['./file-filter.component.scss'],
    imports: [QueryBuilderModule, GridModule],
    providers: [QueryLibraryService]
})


export class FileFilterComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    private translateService: TranslateService,
    private syncfusionTranslationService: SyncfusionTranslationService,
    private fieldAssignmentDefinitionsService: FieldAssignmentDefinitionsService,
    public formsCoordinationService: ImportConfigurationEditFormsCoordinatorService
    
  ) { }

  @Input() public set filterDTO(filter: FilterDTO) {
    this.setInitialRuleModel(filter);
  }
  @Output() public filterUpdated: EventEmitter<FilterDTO> = new EventEmitter<FilterDTO>();
  @ViewChild('querybuilder') public qryBldrObj?: QueryBuilderComponent;
  public showButtons: Object = { ruleDelete: true, groupInsert: true, groupDelete: true };

  private _filterDTO: FilterDTO;
  private filterOperator = FilterOperator;
  public importRules?: RuleModel;
  public allowedOperatorsString = [
    {
      "value": "startswith",
      "key": "Starts With"
    },
    {
      "value": "endswith",
      "key": "Ends With"
    },
    {
      "value": "contains",
      "key": "Contains"
    },
    {
      "value": "equal",
      "key": "Equal"
    },
    {
      "value": "notequal",
      "key": "Not Equal"
    }
  ];
  public allowedOperatorsNumber = [
    {
      "value": "equal",
      "key": "Equal"
    },
    {
      "value": "greaterthanorequal",
      "key": "Greater Than Or Equal"
    },
    {
      "value": "greaterthan",
      "key": "Greater Than"
    },
    {
      "value": "lessthan",
      "key": "Less Than"
    },
    {
      "value": "lessthanorequal",
      "key": "Less Than Or Equal"
    },
    {
      "value": "notequal",
      "key": "Not Equal"
    },
  ];

  public columns:ColumnsModel[] = [];

  private subscription: Subscription;

  ngOnInit(): void {
    this.subscription = this.syncfusionTranslationService.onLanguageChanged.subscribe(() => {
      setTimeout(() => {
        this.translateOperators();
        this.qryBldrObj?.refresh();
      }, 1000);
    });
    const importType = this.formsCoordinationService.configurationFormGeneral.controls.importType.value;
    const targetFieldDefinitions = this.fieldAssignmentDefinitionsService.getTargetDefinitionsImmediately(importType);
    this.columns = targetFieldDefinitions.map(field => {
      return {
        field: field.fieldPath,
        label: field.targetName,
        type: field.fieldType === 'DateTime' ? 'date' : field.fieldType === 'Int32' ? 'number' : 'string' 
      }
    }).sort((a, b) => a.label.localeCompare(b.label));
  }

  ngAfterViewInit(): void {
    this.translateOperators();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private setInitialRuleModel(filter: FilterDTO) {
    const fieldTypes = {};
    this.columns.forEach(c => fieldTypes[c.field] = c.type);
    this.qryBldrObj?.reset();
    if (filter) {
      var ruleModel = {
        'condition': filter.condition === FilterCondition.Or ? 'or' : 'and',
        'rules': []
      }
      ruleModel.rules = this.getRules(filter, fieldTypes);
      this.importRules = ruleModel;
      this.qryBldrObj?.setRules(ruleModel);
    }
    else {
      this.importRules = null;
    };

  }

  private getRules(filter: FilterDTO, fieldTypes: {}): RuleModel[] {
    if (filter.predicates?.length > 0) {
      var ruleModels = [];
      filter.predicates.forEach(p => {
        var ruleModel = {
          'label': p.field,
          'field': p.field,
          'type': p.field ? fieldTypes[p.field] : '',
          'operator': p.operator ? FilterOperator[p.operator].toLocaleLowerCase() : 'equal',
          'value': p.value,
          'rules': []
        };
        if (p.predicates?.length > 0) {
          ruleModel.rules = this.getRules(p, fieldTypes);
        }
        ruleModels.push(ruleModel);
      });
      return ruleModels;
    }
    return null;
  }

  public onRuleChange(args: RuleChangeEventArgs) {
    if (args.rule.rules?.length > 0) {
      if (!this._filterDTO) {
        this._filterDTO = new FilterDTO();
      }
      if (args.rule) {
        this._filterDTO.predicates = this.getPredicates(args.rule);
        if (this._filterDTO.predicates?.length > 0) {
          //Assign the first predicate to the root object
          var predicate = this._filterDTO.predicates[0];
          this._filterDTO.condition = predicate.condition;
          this._filterDTO.field = predicate.field;
          this._filterDTO.isComplex = this._filterDTO.predicates?.length > 1;
          this._filterDTO.operator = predicate.operator;
          this._filterDTO.value = predicate.value;
        }
      }
    }
    this.filterUpdated.emit(this._filterDTO);
  }

  private getPredicates(ruleModel: RuleModel): FilterDTO[] {
    var predicates = [];
    if (ruleModel.rules) {
      ruleModel.rules.forEach(rule => {
        var f = new FilterDTO();
        f.condition = rule.condition === 'or' ? FilterCondition.Or : FilterCondition.And;
        f.operator = this.getDTOOperatorForUIOperator(rule.operator);
        f.field = rule.field;
        f.isComplex = false;
        f.value = rule.value?.toString();
        if (rule.rules?.length > 0) {
          f.isComplex = true;
          f.predicates = this.getPredicates(rule);
        }
        predicates.push(f);
      });

    }
    return predicates;
  }

  private getDTOOperatorForUIOperator(uiOperator: string): FilterOperator {
    if (uiOperator) {
      for (var k in this.filterOperator) {
        if (this.filterOperator[k].toLowerCase() == uiOperator) {
          return this.filterOperator[k] as any as FilterOperator;
        }
      }
    }
    return null;
  }

  private translateOperators() {
    this.allowedOperatorsString = [
      {
        "value": "startswith",
        "key": this.translateService.instant('i18n.domain.import-configuration.edit.file.filter.operators.startswith')
      },
      {
        "value": "endswith",
        "key": this.translateService.instant('i18n.domain.import-configuration.edit.file.filter.operators.endswith')
      },
      {
        "value": "contains",
        "key": this.translateService.instant('i18n.domain.import-configuration.edit.file.filter.operators.contains')
      },
      {
        "value": "equal",
        "key": this.translateService.instant('i18n.domain.import-configuration.edit.file.filter.operators.equal')
      },
      {
        "value": "notequal",
        "key": this.translateService.instant('i18n.domain.import-configuration.edit.file.filter.operators.notequal')
      }
    ];
    this.allowedOperatorsNumber = [
      {
        "value": "equal",
        "key": this.translateService.instant('i18n.domain.import-configuration.edit.file.filter.operators.equal')
      },
      {
        "value": "greaterthanorequal",
        "key": this.translateService.instant('i18n.domain.import-configuration.edit.file.filter.operators.greaterthanorequal')
      },
      {
        "value": "greaterthan",
        "key": this.translateService.instant('i18n.domain.import-configuration.edit.file.filter.operators.greaterthan')
      },
      {
        "value": "lessthan",
        "key": this.translateService.instant('i18n.domain.import-configuration.edit.file.filter.operators.lessthan')
      },
      {
        "value": "lessthanorequal",
        "key": this.translateService.instant('i18n.domain.import-configuration.edit.file.filter.operators.lessthanorequal')
      },
      {
        "value": "notequal",
        "key": this.translateService.instant('i18n.domain.import-configuration.edit.file.filter.operators.notequal')
      },
    ]
  }
}
